// src/components/webinar_funnel/sections/KeyInsights.jsx
import React, { useMemo } from 'react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import {
  TrophyIcon,
  ArrowTrendingUpIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';

// ─── Radial progress ring ────────────────────────────────────────────────────
const RadialProgress = ({ value, label, subtitle, color, size = 100 }) => {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(parseFloat(value) || 0, 100);
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#f1f2f8" strokeWidth="8" />
          <circle
            cx={size / 2} cy={size / 2} r={radius} fill="none"
            stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={offset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold text-darkblue-500">{value}%</span>
        </div>
      </div>
      <span className="text-xs font-semibold text-darkblue-500 mt-2 text-center">{label}</span>
      <span className="text-[10px] text-grey-500 text-center">{subtitle}</span>
    </div>
  );
};

// ─── Hero stat card with sparkline ───────────────────────────────────────────
const HeroStat = ({ label, value, sparkData, color, bgFrom, bgTo, textColor = 'text-white' }) => (
  <div
    className="relative rounded-xl p-5 overflow-hidden"
    style={{ background: `linear-gradient(135deg, ${bgFrom}, ${bgTo})` }}
  >
    {/* Sparkline background */}
    <div className="absolute bottom-0 left-0 right-0 h-16 opacity-20">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={sparkData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
          <Area type="monotone" dataKey="v" stroke="rgba(255,255,255,0.6)" fill="rgba(255,255,255,0.3)" strokeWidth={1.5} dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
    {/* Content */}
    <div className="relative z-10">
      <div className={`text-xs font-semibold uppercase tracking-wide ${textColor} opacity-80 mb-1`}>{label}</div>
      <div className={`text-2xl sm:text-3xl font-bold ${textColor} leading-tight`}>{value}</div>
    </div>
  </div>
);

// ─── Main component ──────────────────────────────────────────────────────────
const KeyInsights = ({ data }) => {
  const insights = useMemo(() => {
    if (!data || data.length === 0) return null;

    const sorted = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));

    const totals = data.reduce(
      (acc, c) => ({
        totalRevenue:       acc.totalRevenue       + (c.sales.revenue || 0),
        totalRegistrations: acc.totalRegistrations + (c.registrations.total || 0),
        totalAttendees:     acc.totalAttendees     + (c.attendees.total || 0),
        totalBookings:      acc.totalBookings      + (c.bookings.total || 0),
        totalSales:         acc.totalSales         + (c.sales.total || 0),
      }),
      { totalRevenue: 0, totalRegistrations: 0, totalAttendees: 0, totalBookings: 0, totalSales: 0 },
    );

    const topCampaign = data.reduce((max, c) =>
      c.registrations.total > max.registrations.total ? c : max,
    );

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const recentCampaigns = data.filter((c) => new Date(c.date) >= thirtyDaysAgo);

    const recentTopPerformer =
      recentCampaigns.length > 0
        ? recentCampaigns.reduce((max, c) =>
            c.registrations.total > max.registrations.total ? c : max,
          )
        : null;

    const avgRegRate = (
      data.reduce((s, c) => s + parseFloat(c.conversions.inviteToReg || 0), 0) / data.length
    ).toFixed(1);

    const avgAttnRate = (
      data.reduce((s, c) => s + parseFloat(c.conversions.regToAttend || 0), 0) / data.length
    ).toFixed(1);

    const avgBookRate = (
      data.reduce((s, c) => s + parseFloat(c.conversions.attendToBook || 0), 0) / data.length
    ).toFixed(1);

    // Sparkline data arrays
    const revenueSparkData   = sorted.map(c => ({ v: c.sales.revenue || 0 }));
    const regSparkData       = sorted.map(c => ({ v: c.registrations.total || 0 }));
    const attendeeSparkData  = sorted.map(c => ({ v: c.attendees.total || 0 }));
    const salesSparkData     = sorted.map(c => ({ v: c.sales.total || 0 }));

    // Best ROI campaign — highest revenue per invite
    const bestROI = data.reduce((best, c) => {
      const rpi = c.invitations > 0 ? (c.sales.revenue || 0) / c.invitations : 0;
      const bestRpi = best.invitations > 0 ? (best.sales.revenue || 0) / best.invitations : 0;
      return rpi > bestRpi ? c : best;
    });
    const bestROIValue = bestROI.invitations > 0
      ? ((bestROI.sales.revenue || 0) / bestROI.invitations).toFixed(2)
      : '0';

    return {
      totals, topCampaign, recentTopPerformer, bestROI, bestROIValue,
      avgRegRate, avgAttnRate, avgBookRate,
      campaignCount: data.length,
      revenueSparkData, regSparkData, attendeeSparkData, salesSparkData,
    };
  }, [data]);

  if (!insights) return null;

  const { totals, topCampaign, recentTopPerformer, bestROI, bestROIValue, avgRegRate, avgAttnRate, avgBookRate } = insights;

  const fmtRevenue = (n) =>
    n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
    : n >= 1_000   ? `$${(n / 1_000).toFixed(0)}K`
    : `$${n}`;

  return (
    <div className="space-y-4">

      {/* ── Hero stats with sparklines ───────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <HeroStat
          label="Total Revenue"
          value={fmtRevenue(totals.totalRevenue)}
          sparkData={insights.revenueSparkData}
          bgFrom="#152451" bgTo="#0093cf"
        />
        <HeroStat
          label="Registrations"
          value={totals.totalRegistrations.toLocaleString()}
          sparkData={insights.regSparkData}
          bgFrom="#92278f" bgTo="#c77ec2"
        />
        <HeroStat
          label="Attendees"
          value={totals.totalAttendees.toLocaleString()}
          sparkData={insights.attendeeSparkData}
          bgFrom="#009391" bgTo="#24f4eb"
        />
        <HeroStat
          label="Total Sales"
          value={totals.totalSales.toLocaleString()}
          sparkData={insights.salesSparkData}
          bgFrom="#f58025" bgTo="#f68c43"
        />
      </div>

      {/* ── Conversion rates + highlights ─────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        {/* Radial progress rings */}
        <div className="md:col-span-5 bg-white border border-grey-200 rounded-xl p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-grey-500 mb-4">Avg Conversion Rates</div>
          <div className="flex items-center justify-around">
            <RadialProgress value={avgRegRate} label="Registration" subtitle="Invite → Reg" color="#92278f" />
            <RadialProgress value={avgAttnRate} label="Attendance" subtitle="Reg → Attend" color="#009391" />
            <RadialProgress value={avgBookRate} label="Booking" subtitle="Attend → Book" color="#f58025" />
          </div>
        </div>

        {/* Highlight cards */}
        <div className="md:col-span-7 space-y-3">
          {/* Top campaign */}
          <div className="bg-white border border-grey-200 rounded-xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-yellow-50 flex items-center justify-center flex-shrink-0">
              <TrophyIcon className="w-5 h-5 text-yellow-500" />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wide text-grey-500">Top Performing Campaign</div>
              <div className="text-base font-bold text-darkblue-500">Campaign {topCampaign.id}</div>
              <div className="text-xs text-grey-500">
                {topCampaign.registrations.total.toLocaleString()} registrations · {topCampaign.attendees.total} attendees · {topCampaign.bookings.total} bookings
              </div>
            </div>
          </div>

          {/* Best ROI campaign */}
          <div className="bg-white border border-grey-200 rounded-xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0">
              <BanknotesIcon className="w-5 h-5 text-teal-600" />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wide text-grey-500">Best ROI Campaign</div>
              <div className="text-base font-bold text-darkblue-500">Campaign {bestROI.id} <span className="text-sm font-normal text-teal-600">· ${bestROIValue}/invite</span></div>
              <div className="text-xs text-grey-500">
                {fmtRevenue(bestROI.sales?.revenue || 0)} revenue · {(bestROI.invitations || 0).toLocaleString()} invites · {bestROI.sales?.total || 0} sales
              </div>
            </div>
          </div>

          {/* Recent standout */}
          {recentTopPerformer && (
            <div className="bg-brightblue-50 border border-brightblue-200 rounded-xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-brightblue-100 flex items-center justify-center flex-shrink-0">
                <ArrowTrendingUpIcon className="w-5 h-5 text-brightblue-600" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-brightblue-600">Recent Standout</div>
                <div className="text-base font-bold text-brightblue-700">Campaign {recentTopPerformer.id}</div>
                <div className="text-xs text-brightblue-600">
                  {recentTopPerformer.registrations.total.toLocaleString()} registrations · {recentTopPerformer.attendees.total} attendees
                  · {new Date(recentTopPerformer.date).toLocaleDateString()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KeyInsights;